import { getConfig } from "./config";
import { isDoorMockEnabled, isWsMockEnabled } from "./utils/doorMock";
import { addBreadcrumb, recordIncrement } from "./utils/observability";

const INITIAL_RECONNECT_TIME = 1000;
const MAX_RECONNECT_TIME = 10000;
const RECONNECT_METRIC_MIN_INTERVAL_MS = 60000;
let reconnectTime = INITIAL_RECONNECT_TIME;
const lastReconnectMetricAt: Record<string, number> = {};

const recordReconnectMetric = (reason: string) => {
  const now = Date.now();
  const lastRecordedAt = lastReconnectMetricAt[reason] ?? 0;
  if (now - lastRecordedAt < RECONNECT_METRIC_MIN_INTERVAL_MS) return;

  if (recordIncrement("locker.ws.reconnects", 1, { reason })) {
    lastReconnectMetricAt[reason] = now;
  }
};

export function connect() {
  if (isWsMockEnabled()) {
    console.log("[MOCK] WebSocket connection disabled");
    addBreadcrumb("ws", "mock connection enabled", undefined, "info");
    return {
      close: () => undefined,
    };
  }

  const { wsUrl, apiKey } = getConfig();
  let heartbeatInterval: NodeJS.Timer | null | undefined;
  const HEARTBEAT_INTERVAL = 30000; // 30 seconds
  const MAX_MISSED_HEARTBEATS = 2;
  let missedHeartbeats = 0;
  let pendingReconnectReason: string | undefined;

  const ws = new WebSocket(`${wsUrl}/ws/locker/${apiKey}`);

  function sendHeartbeat() {
    if (ws.readyState === WebSocket.OPEN) {
      if (process.env.NODE_ENV !== "production") console.log("↑ sending heartbeat");
      ws.send("h");
      missedHeartbeats++;

      // If we've missed too many heartbeats, close the connection
      if (missedHeartbeats > MAX_MISSED_HEARTBEATS) {
        console.error("Too many missed heartbeats, closing connection");
        addBreadcrumb("ws", "missed heartbeats", { missedHeartbeats }, "warning");
        pendingReconnectReason = "heartbeat_timeout";
        ws.close();
      }
    }
  }

  function startHeartbeat() {
    stopHeartbeat(); // Clear any existing interval
    heartbeatInterval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
  }

  function stopHeartbeat() {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
  }

  ws.onopen = function () {
    if (process.env.NODE_ENV !== "production") console.log("ws open");
    addBreadcrumb("ws", "connection opened", { reconnectTime });
    reconnectTime = INITIAL_RECONNECT_TIME;
    startHeartbeat();
  };

  ws.onmessage = async function (e) {
    const message = e.data;
    if (message === "h") {
      if (process.env.NODE_ENV !== "production") console.log("↓ heartbeat response", message);
      // Reset missed heartbeats counter on acknowledgment
      missedHeartbeats = 0;
      return;
    }

    const parsedMessage = JSON.parse(message);

    if (parsedMessage.event && parsedMessage.event.type === "door.open") {
      const position = parsedMessage.event.door;
      const x = position[0];
      const y = position.substring(1);
      try {
        await fetch(`http://127.0.0.1:5000/?xpos=${x}&ypos=${y}`);
      } catch (error) {
        if (isDoorMockEnabled()) {
          console.log(`[MOCK] Door open (${position}) from websocket - hardware unavailable`);
        } else {
          throw error;
        }
      }
    }
  };

  ws.onerror = function () {
    if (process.env.NODE_ENV !== "production") console.error("ws error");
    addBreadcrumb("ws", "connection error", undefined, "warning");
    pendingReconnectReason = "error";
    stopHeartbeat();
    ws.close();
  };

  ws.onclose = function () {
    if (process.env.NODE_ENV !== "production")
      console.error(`ws closed. Reconnection in ${reconnectTime}ms`);
    addBreadcrumb("ws", "connection closed", { reconnectTime }, "warning");
    recordReconnectMetric(pendingReconnectReason ?? "close");
    pendingReconnectReason = undefined;
    stopHeartbeat();

    setTimeout(function () {
      reconnectTime = Math.min(reconnectTime * 1.1, MAX_RECONNECT_TIME);
      connect();
    }, reconnectTime);
  };

  window.onbeforeunload = function () {
    if (ws && ws.readyState === ws.OPEN) {
      stopHeartbeat();
      ws.close();
    }
  };

  return {
    close: () => {
      stopHeartbeat();
      ws.close();
    },
  };
}
